import { datadogLogs } from '@datadog/browser-logs';
import {
  DATADOG_BUILD_TAG_KEY,
  getDatadogVersion,
} from 'integrations/datadog/datadogVersion';
import { addConsoleDumpLogsEvent } from './consoleDump';
import { shouldSendLog } from './shouldSendLog';

type Logger = {
  debug: (
    message: string,
    messageContext?: { details: object },
    error?: Error
  ) => void;
  info: (
    message: string,
    messageContext?: { details: object },
    error?: Error
  ) => void;
  warn: (
    message: string,
    messageContext?: { details: object },
    error?: Error
  ) => void;
  error: (
    message: string,
    messageContext?: { details: object },
    error?: Error
  ) => void;
};

let loggerInstance: Logger | undefined;

const PREFIX = '[ph-web]';

function createPrefixedConsoleLogger(): Logger {
  // Using bind to preserve the stack trace
  return {
    debug: console.debug.bind(console, PREFIX),
    info: console.info.bind(console, PREFIX),
    warn: console.warn.bind(console, PREFIX),
    error: console.error.bind(console, PREFIX),
  };
}

function init(): Logger {
  if (loggerInstance) {
    return loggerInstance;
  }

  if (
    import.meta.env.MODE !== 'test' &&
    import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_CLIENT_TOKEN &&
    import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_ENV &&
    import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_SERVICE &&
    import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_SITE
  ) {
    datadogLogs.init({
      clientToken: import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_CLIENT_TOKEN,
      env: import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_ENV,
      service: import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_SERVICE,
      site: import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_SITE,
      version: getDatadogVersion(),
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      beforeSend: (logsEvent, context) => {
        if (!shouldSendLog(logsEvent, context)) {
          return false;
        }

        if (
          import.meta.env.PUBLIC_DATADOG_BROWSER_LOGS_ENABLE_CONSOLE_DUMP ===
          'true'
        ) {
          // Storing events to allow dumping console messages on debug screen for cases where
          // access to browser console is not possible/convenient.
          // The dump holds only events that are sent.
          addConsoleDumpLogsEvent(logsEvent);
        }

        return true;
      },
    });

    if (import.meta.env.PUBLIC_BUILD_TAG) {
      datadogLogs.setGlobalContextProperty(
        DATADOG_BUILD_TAG_KEY,
        import.meta.env.PUBLIC_BUILD_TAG
      );
    }

    const logger = datadogLogs.createLogger('ph-web', {
      level: 'debug',
      handler: ['http', 'console'],
      context: {
        // Custom context property is used here because it allows filtering messages in devtools console
        // (unlike the nested logger.name property).
        //
        // WARN: Datadog logger functions don't support bind technique to add a prefix to message (unlike console).
        // Do not attempt to add wrapper functions for prefixing a message, log entries would point to the wrapper
        // function instead of the line that called the logger!
        prefix: PREFIX,
      },
    });

    loggerInstance = logger;
    return loggerInstance;
  }

  loggerInstance = createPrefixedConsoleLogger();
  return loggerInstance;
}

export const logger = init();
